
import React, { useState, useMemo } from 'react';
import {
  Search,
  FilterList,
  ArrowBack,
  ShoppingCart,
} from '@mui/icons-material';
import axios from 'axios';
import { post, get } from '../../utility/fetchWeb';
import DrugDetailsModal from './DrugDetailsModal';
import DrugSearchResultCard from './DrugSearchResultCard';
import CheckoutModal from './CheckoutModal';
import LaboratoryChatModal from '../modals/LaboratoryChatModal';
import VideoCalling from '../Layout/VideoCalling';
import PromoBanner from '../Layout/PromoBanner';
import Pagination from '../Layout/Pagination';

const PAGE_SIZE = 10;

export default function DrugSearchSection({ initialCategory = 'pharmacy' }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [error, setError] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState(initialCategory); // all, pharmacy, lab, equipment, beds, services
  const [categorizedResults, setCategorizedResults] = useState({
    pharmacyItems: [],
    labServices: [],
    equipments: [],
    beds: [],
    categoryItems: []
  });
  const [targetedAds, setTargetedAds] = useState([]);

  // Fetch targeted ads
  const getTargetedAds = async () => {
    try {
      const userId = sessionStorage.getItem('userId');

      // Fetch both Marketplace and all location ads
      const marketplaceResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/connecthealthadmin/api/ad/find-ad?Id=${userId}&page=Marketplace`);
      const allResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/connecthealthadmin/api/ad/find-ad?Id=${userId}&page=All Locations`);

      const marketplaceAds = marketplaceResponse.ok ? await marketplaceResponse.json() : [];
      const allAds = allResponse.ok ? await allResponse.json() : [];

      // Merge and transform ads
      const mergedAds = [...marketplaceAds, ...allAds];
      const transformedAds = mergedAds.map(ad => ({
        id: ad.id,
        imageUrl: ad.bannerImageUrl,
        title: ad.title,
        subtitle: ad.description,
        redirectUrl: ad.redirectUrl,
        status: 1,
        isTargeted: true
      }));

      setTargetedAds(transformedAds);
    } catch (error) {
      console.error('Error fetching targeted ads:', error);
      setTargetedAds([]);
    }
  };

  // Fetch ads on mount
  React.useEffect(() => {
    getTargetedAds();
  }, []);

  // Update category filter when initialCategory prop changes and trigger search
  React.useEffect(() => {
    setCategoryFilter(initialCategory);
    // If there's a search term, re-search with the new category
    if (searchTerm.trim()) {
      handleSearch(searchTerm, 1, initialCategory);
    }
  }, [initialCategory]);

  // Chat and video call states
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [selectedPharmacyForChat, setSelectedPharmacyForChat] = useState(null);
  const [showVideoChatModal, setShowVideoChatModal] = useState(false);
  const [startAudioOnly, setStartAudioOnly] = useState(false);
  const [appId, setAppId] = useState(process.env.REACT_APP_AGORA_KEY);
  const [channel, setChannel] = useState("call");
  const [token, setToken] = useState(null);
  const [uid, setUid] = useState(sessionStorage.getItem("userId") || "defaultUid");
  const [selectedPharmacyForCall, setSelectedPharmacyForCall] = useState(null);
  const [callId, setCallId] = useState(null);

  // Fetch data from API
  const handleSearch = async (searchQuery = searchTerm, pageNum = page, filterCategory = categoryFilter) => {
    if (!searchQuery.trim()) {
      setHasSearched(false);
      return;
    }

    setHasSearched(true);
    setLoading(true);
    setError('');
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL_WEB;
      const response = await axios.get(`${baseUrl}/eKiosk/search`, {
        params: {
          searchTerm: searchQuery,
          page: pageNum,
          pageSize: PAGE_SIZE,
        },
      });

      const data = response.data;

      // Store categorized results
      setCategorizedResults({
        pharmacyItems: data?.pharmacyItems?.items || [],
        labServices: data?.labServices?.items || [],
        equipments: data?.equipments?.items || [],
        beds: data?.beds?.items || [],
        categoryItems: data?.categoryItems?.items || []
      });

      // Combine all items based on filter
      let allItems = [];
      let relevantTotalPages = 0;
      let relevantTotalCount = 0;

      if (filterCategory === 'all') {
        allItems = [
          ...(data?.pharmacyItems?.items || []).map(item => ({ ...item, categoryType: 'medication' })),
          ...(data?.labServices?.items || []).map(item => ({ ...item, categoryType: 'lab' })),
          ...(data?.beds?.items || []).map(item => ({ ...item, categoryType: 'bed' })),
          ...(data?.equipments?.items || []).map(item => ({ ...item, categoryType: 'equipment' })),
          ...(data?.categoryItems?.items || []).map(item => ({ ...item, categoryType: 'service' })),
        ];
        relevantTotalPages = Math.max(
          data?.pharmacyItems?.totalPages || 0,
          data?.labServices?.totalPages || 0,
          data?.equipments?.totalPages || 0,
          data?.beds?.totalPages || 0,
          data?.categoryItems?.totalPages || 0
        );
        relevantTotalCount = allItems.length;
      } else if (filterCategory === 'pharmacy') {
        allItems = (data?.pharmacyItems?.items || []).map(item => ({ ...item, categoryType: 'medication' }));
        relevantTotalPages = data?.pharmacyItems?.totalPages || 0;
        relevantTotalCount = data?.pharmacyItems?.totalCount || 0;
      } else if (filterCategory === 'lab') {
        allItems = (data?.labServices?.items || []).map(item => ({ ...item, categoryType: 'lab' }));
        relevantTotalPages = data?.labServices?.totalPages || 0;
        relevantTotalCount = data?.labServices?.totalCount || 0;
      } else if (filterCategory === 'equipment') {
        allItems = (data?.equipments?.items || []).map(item => ({ ...item, categoryType: 'equipment' }));
        relevantTotalPages = data?.equipments?.totalPages || 0;
        relevantTotalCount = data?.equipments?.totalCount || 0;
      } else if (filterCategory === 'beds') {
        allItems = (data?.beds?.items || []).map(item => ({ ...item, categoryType: 'bed' }));
        relevantTotalPages = data?.beds?.totalPages || 0;
        relevantTotalCount = data?.beds?.totalCount || 0;
      } else if (filterCategory === 'services') {
        allItems = (data?.categoryItems?.items || []).map(item => ({ ...item, categoryType: 'service' }));
        relevantTotalPages = data?.categoryItems?.totalPages || 0;
        relevantTotalCount = data?.categoryItems?.totalCount || 0;
      }

      setResults(allItems);
      setTotalPages(relevantTotalPages);
      setTotalCount(relevantTotalCount);
      
      // Scroll to results section after data is loaded
      setTimeout(() => {
        const resultsSection = document.getElementById('results-section');
        if (resultsSection) {
          resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } catch (err) {
      setError('Failed to fetch results. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial search on mount - remove this
  // React.useEffect(() => {
  //   handleSearch();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const addToCart = (drug) => {
    const existing = cart.find((i) => i.id === drug.id);
    if (existing) {
      setCart((c) =>
        c.map((i) => (i.id === drug.id ? { ...i, qty: (i.qty || 1) + 1 } : i))
      );
    } else {
      setCart((c) => [...c, { ...drug, qty: 1 }]);
    }
  };

  const updateCartQuantity = (itemId, newQty) => {
    if (newQty < 1) return;
    setCart((c) =>
      c.map((i) => (i.id === itemId ? { ...i, qty: newQty } : i))
    );
  };

  const removeFromCart = (itemId) => {
    setCart((c) => c.filter((i) => i.id !== itemId));
  };

  const clearCart = () => {
    setCart([]);
  };

  // Agora token generation
  const getAgoraDetails = async (receiverId) => {
    try {
      const res = await get(`/posts/generate-new-token?channelName=${`connecthealthpro_videocall-${uid}&${receiverId}`}&uid=${uid}`);
      if (res && res.token) {
        setToken(res.token);
        setUid(Number(res.uid) || uid);
        setAppId(res.appId || process.env.REACT_APP_AGORA_KEY);
        setChannel(res.channelName || "call");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error fetching Agora token:", error);
      return false;
    }
  };

  // Notify caller
  const notifyCaller = async (chann, id) => {
    try {
      const res = await post(`/Posts/notify-call`, {
        receiverId: parseInt(id),
        channelName: chann,
        callerId: Number(sessionStorage.getItem("userId")),
        callerName: sessionStorage.getItem("name"),
        userType: 2 // for practitioner 1 for patient
      });
      if (res?.data) {
        console.log("Caller notified successfully");
      }
    } catch (error) {
      console.error("Error notifying caller:", error);
    }
  };

  // Handle video call
  const handleVideoCall = async (pharmacy) => {
    setSelectedPharmacyForCall(pharmacy);
    const success = await getAgoraDetails(pharmacy.contactPersonId);
    if (success) {
      setStartAudioOnly(false);
      setCallId(Date.now());
      setShowVideoChatModal(true);
      notifyCaller(`connecthealthpro_videocall-${uid}&${pharmacy.contactPersonId}`, pharmacy.contactPersonId);
    }
  };

  // Handle audio call
  const handleAudioCall = async (pharmacy) => {
    setSelectedPharmacyForCall(pharmacy);
    const success = await getAgoraDetails(pharmacy.contactPersonId);
    if (success) {
      setStartAudioOnly(true);
      setCallId(Date.now());
      setShowVideoChatModal(true);
      notifyCaller(`connecthealthpro_audiocall-${uid}&${pharmacy.contactPersonId}`, pharmacy.contactPersonId);
    }
  };

  // Handle chat
  const handleMessageClick = (pharmacy) => {
    setSelectedPharmacyForChat(pharmacy);
    setIsChatModalOpen(true);
  };

  // Close video chat
  const handleCloseVideoChat = () => {
    setShowVideoChatModal(false);
    setToken(null);
    setSelectedPharmacyForCall(null);
    setCallId(null);
  };

  const categories = useMemo(() => {
    const map = {};
    results.forEach((item) => {
      if (!item.productName) return; // Skip items without productName
      const first = item.productName.split(' ')[0];
      const key = first.length > 3 ? first : item.productName.split(' ').slice(0, 2).join(' ');
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    return map;
  }, [results]);

  const displayed = selectedCategory ? categories[selectedCategory] ?? [] : results;

  // Get category display name
  const getCategoryDisplayName = () => {
    const categoryNames = {
      'all': 'All Products',
      'pharmacy': 'Medications',
      'lab': 'Lab Services',
      'equipment': 'Medical Equipment',
      'beds': 'Hospital Beds',
      'services': 'Medical Services'
    };
    return categoryNames[categoryFilter] || 'All Products';
  };

  return (
    <div className="w-100 bg-gray-50 min-h-screen">
      {/* Sticky Header - positioned below app header */}
      <header className="bg-white shadow-sm sticky top-[16px] z-20">
        <div className="w-100 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex sm:flex-row sm:items-center sm:justify-between gap-3 py-3 sm:py-4">
            <div className="flex items-center space-x-3">
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Marketplace
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                    {getCategoryDisplayName()}
                  </span>
                </div>
              </div>
            </div>

            <div className="relative">
              <button
                onClick={() => setShowCheckout(true)}
                disabled={cart.length === 0}
                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center transition-colors shadow-md ${
                  cart.length === 0
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                <ShoppingCart className={cart.length === 0 ? 'text-gray-500' : 'text-white'} sx={{ fontSize: 20 }} />
              </button>
              {cart.reduce((s, i) => s + (i.qty || 1), 0) > 0 && (
                <span className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 bg-red-500 text-white text-xs font-bold w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center border-2 border-white text-[10px] sm:text-xs">
                  {cart.reduce((s, i) => s + (i.qty || 1), 0)}
                </span>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              handleSearch(searchTerm, 1, categoryFilter);
            }}
            className="pb-3 sm:pb-4"
          >
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" sx={{ fontSize: 20 }} />
                <input
                  type="text"
                  placeholder={`Search for ${categoryFilter === 'pharmacy' ? 'medications' : categoryFilter === 'lab' ? 'lab services' : categoryFilter === 'equipment' ? 'medical equipment' : categoryFilter === 'beds' ? 'hospital beds' : categoryFilter === 'services' ? 'medical services' : 'products'}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-3.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white text-gray-900 placeholder-gray-400 text-sm sm:text-base transition-all"
                  style={{ border: '1px solid #9ca3af' }}
                />
              </div>
            </div>
            {/* Results Info */}
            {hasSearched && results.length > 0 && !loading && (
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Found <span className="font-semibold text-gray-900">{totalCount}</span> results
                {searchTerm && ` for "${searchTerm}"`}
              </p>
            </div>
            )}
          </form>
        </div>
      </header>

      {/* Advertisement Banner */}
      {targetedAds.length > 0 && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <PromoBanner ads={targetedAds} page="Marketplace" />
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-t-4 border-b-4 border-green-600"></div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg">
            <p className="font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* No Results State */}
      {!loading && hasSearched && results.length === 0 && searchTerm && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-center text-gray-500 text-sm sm:text-base">
            No results found. Try another search term.
          </p>
        </div>
      )}

      {/* Main Content - Results Section */}
      <main
        className="bg-gray-50"
        id="results-section"
      >
        {hasSearched && results.length > 0 && !loading && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
            {/* Category Pills */}
            {Object.keys(categories).length > 1 && (
              <div className="mb-5 sm:mb-6">
                <div className="flex space-x-2 sm:space-x-3 overflow-x-auto pb-2 scrollbar-hide">
                  {Object.keys(categories).map((cat) => (
                    <button
                      key={cat}
                      onClick={() =>
                        setSelectedCategory((c) => (c === cat ? null : cat))
                      }
                      className={`flex-shrink-0 px-3.5 py-2 sm:px-5 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${selectedCategory === cat
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-white text-green-700 border-2 border-green-200 hover:border-green-400'
                        }`}
                    >
                      {cat} ({categories[cat].length})
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Responsive Grid - 3 columns with space between */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-104 justify-items-center">
              {displayed.map((drug) => (
                <DrugSearchResultCard
                  key={drug.id}
                  drug={drug}
                  onClick={() => setSelectedDrug(drug)}
                  onAddToCart={() => addToCart(drug)}
                  isInCart={cart.some((i) => i.id === drug.id)}
                  onVideoCall={() => handleVideoCall(drug)}
                  onAudioCall={() => handleAudioCall(drug)}
                  onMessage={() => handleMessageClick(drug)}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(newPage) => {
                  setPage(newPage);
                  handleSearch(searchTerm, newPage, categoryFilter);
                }}
              />
            )}
          </div>
        )}
      </main>

      {/* Modal */}
      <DrugDetailsModal
        open={!!selectedDrug}
        drug={selectedDrug}
        onClose={() => setSelectedDrug(null)}
        onAddToCart={() => selectedDrug && addToCart(selectedDrug)}
      />

      {/* Checkout Modal */}
      <CheckoutModal
        open={showCheckout}
        cart={cart}
        onClose={() => setShowCheckout(false)}
        onUpdateQuantity={updateCartQuantity}
        onRemoveItem={removeFromCart}
        onClearCart={clearCart}
      />

      {/* Chat Modal */}
      <LaboratoryChatModal
        open={isChatModalOpen}
        handleClose={() => {
          setIsChatModalOpen(false);
          setSelectedPharmacyForChat(null);
        }}
        selectedLab={selectedPharmacyForChat}
      />

      {/* Video/Audio Call Modal */}
      {showVideoChatModal && selectedPharmacyForCall && (
        <VideoCalling
          appId={appId}
          channel={channel}
          token={token}
          uid={uid}
          callId={callId}
          open={showVideoChatModal}
          onClose={handleCloseVideoChat}
          startWithVideoOff={startAudioOnly}
          isAudio={startAudioOnly}
        />
      )}
    </div>
  );
}
